import { useState, useEffect, useMemo } from "react";
import {
  createRoutine,
  updateRoutine,
  deleteRoutine,
  completeRoutineItem,
  getRoutinesByUser,
  useQuery,
  useAction,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import type { TradingRoutine, RoutineCompletion } from "wasp/entities";
import { Plus, Edit, Trash2, Check, X, Sparkles } from "lucide-react";
import { PaymentPlanId, SubscriptionStatus } from "../payment/plans";
import PlanSelectionModal from "../dashboard/PlanSelectionModal";
import { Button } from "../client/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../client/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import { Switch } from "../client/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { Textarea } from "../client/components/ui/textarea";
import { Checkbox } from "../client/components/ui/checkbox";
import { toast } from "../client/hooks/use-toast";
import GlobalRoutineHeatmap from "./GlobalRoutineHeatmap";
import useTranslation from "../client/hooks/useTranslation";
import { BeamButton } from "../client/components/ui/beam-button";
import { motion } from "framer-motion";
import { trackRoutineCreated, trackRoutineCompleted } from '../client/utils/posthog';
import AnimatedMetric from "../client/components/AnimatedMetric";
import { metricsContainerVariants, metricsItemVariants } from "../client/components/motionVariants";
// Date formatting helper
const formatDate = (date: Date): string => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

interface RoutineFormData {
  name: string;
  description: string;
  checklistItems: string[];
  frequency: "daily" | "weekly" | "monthly" | "custom";
  isActive: boolean;
}

const initialFormData: RoutineFormData = {
  name: "",
  description: "",
  checklistItems: [""],
  frequency: "daily",
  isActive: true,
};

export default function TradingRoutinesPage({ sectionTitle }: { sectionTitle?: string } = {}) {
  const t = useTranslation();
  const { data: user } = useAuth();
  const [showPlanModal, setShowPlanModal] = useState(false);
  const isFreePlan = useMemo(() => {
    if (!user) return false;
    if (!user.subscriptionPlan || user.subscriptionPlan === PaymentPlanId.Free) return true;
    if (!user.subscriptionStatus || user.subscriptionStatus === SubscriptionStatus.Deleted) return true;
    return false;
  }, [user]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingRoutine, setEditingRoutine] = useState<TradingRoutine | null>(
    null,
  );
  const [formData, setFormData] = useState<RoutineFormData>(initialFormData);

  const createRoutineFn = useAction(createRoutine);
  const updateRoutineFn = useAction(updateRoutine);
  const deleteRoutineFn = useAction(deleteRoutine);
  const completeRoutineItemFn = useAction(completeRoutineItem);

  const { data: routines, isLoading, refetch } = useQuery(getRoutinesByUser);

  const handleCreate = async () => {
    // Validation côté client pour des messages immédiats
    if (!formData.name || formData.name.trim() === "") {
      toast({
        title: t.common.error,
        description: t.validation.fieldRequired.replace("{field}", t.routines.name),
        variant: "destructive",
      });
      return;
    }
    
    const validItems = formData.checklistItems.filter((item) => item.trim() !== "");
    if (validItems.length === 0) {
      toast({
        title: t.common.error,
        description: t.validation.fieldRequired.replace("{field}", t.routines.checklistItems),
        variant: "destructive",
      });
      return;
    }

    try {
      await createRoutineFn({
        name: formData.name,
        description: formData.description || null,
        checklistItems: validItems,
        frequency: formData.frequency,
        isActive: formData.isActive,
      });
      trackRoutineCreated();
      toast({
        title: t.routines.routineCreated,
        description: "La routine a été créée avec succès.",
      });
      setIsCreateDialogOpen(false);
      setFormData(initialFormData);
      refetch();
    } catch (error: any) {
      // Le message d'erreur du serveur est déjà formaté et spécifique
      toast({
        title: t.common.error,
        description: error.message || t.routines.cannotCreate,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (routine: TradingRoutine) => {
    setEditingRoutine(routine);
    setFormData({
      name: routine.name,
      description: routine.description || "",
      checklistItems:
        routine.checklistItems.length > 0
          ? routine.checklistItems
          : [""],
      frequency: routine.frequency as "daily" | "weekly" | "monthly" | "custom",
      isActive: routine.isActive,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingRoutine) return;

    // Validation côté client pour des messages immédiats
    if (!formData.name || formData.name.trim() === "") {
      toast({
        title: t.common.error,
        description: t.validation.fieldRequired.replace("{field}", t.routines.name),
        variant: "destructive",
      });
      return;
    }
    
    const validItems = formData.checklistItems.filter((item) => item.trim() !== "");
    if (validItems.length === 0) {
      toast({
        title: t.common.error,
        description: t.validation.fieldRequired.replace("{field}", t.routines.checklistItems),
        variant: "destructive",
      });
      return;
    }

    try {
      await updateRoutineFn({
        id: editingRoutine.id,
        name: formData.name,
        description: formData.description || null,
        checklistItems: validItems,
        frequency: formData.frequency,
        isActive: formData.isActive,
      });
      toast({
        title: t.routines.routineUpdated,
        description: "La routine a été modifiée avec succès.",
      });
      setIsEditDialogOpen(false);
      setEditingRoutine(null);
      setFormData(initialFormData);
      refetch();
    } catch (error: any) {
      // Le message d'erreur du serveur est déjà formaté et spécifique
      toast({
        title: t.common.error,
        description: error.message || t.routines.cannotUpdate,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (routine: TradingRoutine) => {
    if (!confirm(t.routines.confirmDelete)) return;

    try {
      await deleteRoutineFn({ id: routine.id });
      toast({
        title: t.routines.routineDeleted,
        description: "La routine a été supprimée avec succès.",
      });
      refetch();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || t.routines.cannotDelete,
        variant: "destructive",
      });
    }
  };

  const handleCompleteItems = async (
    routine: TradingRoutine,
    completedItems: string[],
  ) => {
    try {
      await completeRoutineItemFn({
        routineId: routine.id,
        completedItems,
        date: new Date(),
      });
      trackRoutineCompleted();
      toast({
        title: "Routine complétée",
        description: "Les items ont été marqués comme complétés.",
      });
      await refetch();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de compléter la routine.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="pt-6">
        <div className="text-center">{t.routines.loading}</div>
      </div>
    );
  }

  return (
    <div className="pt-6">
      <motion.div
        className="mb-4 flex items-center justify-between"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
      >
        {sectionTitle && (
          <h2 className="text-xl font-semibold text-muted-foreground uppercase tracking-wider">
            {sectionTitle}
          </h2>
        )}
        <BeamButton size="default" onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t.routines.newRoutine}
        </BeamButton>
      </motion.div>

      {isFreePlan && routines && routines.length >= 1 && (
        <Card className="mb-6 border-primary/50 bg-gradient-to-r from-primary/5 via-background to-primary/5">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-primary/10 p-3">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{t.routines.limitReached}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{t.routines.limitReachedDescription}</p>
                </div>
              </div>
              <BeamButton onClick={() => setShowPlanModal(true)}>
                <Sparkles className="h-4 w-4 mr-2" />
                {t.dashboard.upgradeButton}
              </BeamButton>
            </div>
          </CardContent>
        </Card>
      )}

      {routines && routines.length > 0 && (
        <GlobalRoutineHeatmap routines={routines} />
      )}

      {routines && routines.length > 0 ? (
        <div className="grid grid-cols-1 gap-6">
          {routines.map((routine) => (
            <RoutineCard
              key={routine.id}
              routine={routine}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onComplete={handleCompleteItems}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground mb-4">
              {t.routines.noRoutines}
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t.routines.createFirst}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.routines.newRoutine}</DialogTitle>
            <DialogDescription>
              Créez une nouvelle routine de trading
            </DialogDescription>
          </DialogHeader>
          <RoutineForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleCreate}
            onCancel={() => {
              setIsCreateDialogOpen(false);
              setFormData(initialFormData);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.routines.editRoutine}</DialogTitle>
            <DialogDescription>
              Modifiez les informations de la routine
            </DialogDescription>
          </DialogHeader>
          <RoutineForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleUpdate}
            onCancel={() => {
              setIsEditDialogOpen(false);
              setEditingRoutine(null);
              setFormData(initialFormData);
            }}
          />
        </DialogContent>
      </Dialog>
      <PlanSelectionModal open={showPlanModal} onOpenChange={setShowPlanModal} />
    </div>
  );
}

function RoutineCard({
  routine,
  onEdit,
  onDelete,
  onComplete,
}: {
  routine: TradingRoutine & { routineCompletions: RoutineCompletion[] };
  onEdit: (routine: TradingRoutine) => void;
  onDelete: (routine: TradingRoutine) => void;
  onComplete: (routine: TradingRoutine, items: string[]) => void;
}) {
  const t = useTranslation();
  // Helper function to check if two dates are the same day
  const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Find today's completion
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayCompletion = routine.routineCompletions.find((completion) => {
    const completionDate = new Date(completion.date);
    completionDate.setHours(0, 0, 0, 0);
    return isSameDay(completionDate, today);
  });

  // Items that are already completed today (should be disabled)
  const completedTodayItems = todayCompletion ? new Set(todayCompletion.completedItems) : new Set();

  // Initialize checked items - only new items not already completed today
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());

  // Reset checked items when routine data changes (after refetch)
  useEffect(() => {
    setCheckedItems(new Set());
  }, [routine.id, routine.routineCompletions.length]);

  const handleItemToggle = (item: string) => {
    // Don't allow toggling items that are already completed today
    if (completedTodayItems.has(item)) {
      return;
    }
    const newChecked = new Set(checkedItems);
    if (newChecked.has(item)) {
      newChecked.delete(item);
    } else {
      newChecked.add(item);
    }
    setCheckedItems(newChecked);
  };

  const handleComplete = () => {
    if (checkedItems.size === 0) return;
    // Only send newly checked items (not already completed today)
    // The backend will merge them with existing completion
    onComplete(routine, Array.from(checkedItems));
    // Reset checked items after completion
    setCheckedItems(new Set());
  };

  const lastCompletion = routine.routineCompletions[0];
  const completionRate =
    routine.checklistItems.length > 0
      ? (lastCompletion?.completedItems.length || 0) /
        routine.checklistItems.length
      : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg">{routine.name}</CardTitle>
            {routine.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {routine.description}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(routine)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(routine)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2 text-sm">
          <span
            className={`px-2 py-1 rounded ${
              routine.isActive
                ? "bg-primary/10 text-primary"
                : "bg-muted text-muted-foreground"
            }`}
          >
            {routine.frequency}
          </span>
          {lastCompletion && (
            <span className="text-muted-foreground">
              {t.routines.lastCompletion}: {formatDate(new Date(lastCompletion.date))}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Label>Checklist</Label>
          {routine.checklistItems.map((item, index) => {
            const isCompletedToday = completedTodayItems.has(item);
            const isChecked = checkedItems.has(item);
            return (
              <div key={index} className="flex items-center gap-2">
                <Checkbox
                  checked={isChecked || isCompletedToday}
                  onCheckedChange={() => handleItemToggle(item)}
                  disabled={isCompletedToday}
                />
                <span className={`text-sm ${isCompletedToday ? "text-muted-foreground line-through" : ""}`}>
                  {item}
                  {isCompletedToday && (
                    <span className="ml-2 text-xs text-pnlPositive">{t.routines.completedToday}</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
        {checkedItems.size > 0 && !todayCompletion && (
          <Button
            className="mt-4 w-full"
            onClick={handleComplete}
          >
            <Check className="mr-2 h-4 w-4" />
            {t.routines.markCompleted.replace("{count}", checkedItems.size.toString())}
          </Button>
        )}
        {checkedItems.size > 0 && todayCompletion && (
          <Button
            className="mt-4 w-full"
            onClick={handleComplete}
          >
            <Check className="mr-2 h-4 w-4" />
            Ajouter {checkedItems.size} item(s) supplémentaire(s)
          </Button>
        )}
        {lastCompletion && (
          <motion.div
            key="routines"
            variants={metricsContainerVariants}
            initial="hidden"
            animate="show"
            className="mt-4 text-sm"
          >
            <motion.div variants={metricsItemVariants} className="flex items-center justify-between mb-1">
              <span>{t.routines.completionProgress}</span>
              <AnimatedMetric
                value={completionRate * 100}
                format="integer"
                suffix="%"
              />
            </motion.div>
            <motion.div variants={metricsItemVariants} className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-pnlPositive h-2 rounded-full"
                style={{ width: `${completionRate * 100}%` }}
              />
            </motion.div>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}

function RoutineForm({
  formData,
  setFormData,
  onSubmit,
  onCancel,
}: {
  formData: RoutineFormData;
  setFormData: (data: RoutineFormData) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const t = useTranslation();
  const addChecklistItem = () => {
    setFormData({
      ...formData,
      checklistItems: [...formData.checklistItems, ""],
    });
  };

  const removeChecklistItem = (index: number) => {
    const newItems = formData.checklistItems.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      checklistItems: newItems.length > 0 ? newItems : [""],
    });
  };

  const updateChecklistItem = (index: number, value: string) => {
    const newItems = [...formData.checklistItems];
    newItems[index] = value;
    setFormData({ ...formData, checklistItems: newItems });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="name">{t.routines.name} *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) =>
            setFormData({ ...formData, name: e.target.value })
          }
          required
        />
      </div>

      <div>
        <Label htmlFor="description">{t.routines.description}</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          rows={3}
        />
      </div>

      <div>
        <Label htmlFor="frequency">{t.routines.frequency} *</Label>
        <Select
          value={formData.frequency}
          onValueChange={(value: "daily" | "weekly" | "monthly" | "custom") =>
            setFormData({ ...formData, frequency: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">{t.routines.daily}</SelectItem>
            <SelectItem value="weekly">{t.routines.weekly}</SelectItem>
            <SelectItem value="monthly">{t.routines.monthly}</SelectItem>
            <SelectItem value="custom">{t.routines.custom}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>{t.routines.checklistItems} *</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addChecklistItem}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="space-y-2">
          {formData.checklistItems.map((item, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={item}
                onChange={(e) => updateChecklistItem(index, e.target.value)}
                placeholder={`Item ${index + 1}`}
              />
              {formData.checklistItems.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeChecklistItem(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="isActive"
          checked={formData.isActive}
          onCheckedChange={(v) => setFormData({ ...formData, isActive: v })}
        />
        <Label htmlFor="isActive">{t.routines.isActive}</Label>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          {t.routines.cancel}
        </Button>
        <Button onClick={onSubmit}>{t.routines.save}</Button>
      </DialogFooter>
    </div>
  );
}

